import type { IDisplayedProgrammingRange } from "@/components/piloting/planning/section-programming-ranges-cell.types";
import type { ProgramId } from "@/data/common/ids";
import type { ProgrammingRange } from "@/domains/v4b/site-programming-range/site-programming-range.types";
import type { TDateTime } from "@/utils/time";
import type { Ref } from "vue";

import { isDefined } from "@voltalis/frontend-utils";
import { v4 } from "uuid";
import { toValue } from "vue";

import { defaultProgramColorName } from "@/domains/v4b/program/program.types";
import { brandNumber, brandString } from "@/utils/branded";
import { chain } from "@/utils/chain";
import $date from "@/utils/date";

function isProgrammingRangeStartTimeIncludedInRange(
  programmingRange: ProgrammingRange,
  startDateTime: TDateTime,
  endDateTime: TDateTime,
): boolean {
  const programmingStartTime = programmingRange.start;
  return programmingStartTime >= startDateTime && programmingStartTime <= endDateTime;
}

function isProgrammingRangeEndTimeIncludedInRange(
  programmingRange: ProgrammingRange,
  startDateTime: TDateTime,
  endDateTime: TDateTime,
): boolean {
  const programmingEndTime = programmingRange.end;
  if (!programmingEndTime) return false;
  return programmingEndTime >= startDateTime && programmingEndTime <= endDateTime;
}

export function isDateTimeIncludedInProgrammingRange(programmingRange: ProgrammingRange, dateTime: TDateTime): boolean {
  const programmingStartTime = programmingRange.start;
  const programmingEndTime = programmingRange.end;
  return dateTime >= programmingStartTime && (!programmingEndTime || dateTime <= programmingEndTime);
}

export function isProgrammingRangePassed(programmingRange: Pick<ProgrammingRange, "end">, now: Date): boolean {
  if (!isDefined(programmingRange.end)) return false;

  return $date.getDateMilliseconds(programmingRange.end) < $date.getDateMilliseconds(now);
}

function isRangeIncludedInProgrammingRange(
  programmingRange: ProgrammingRange,
  startDateTime: TDateTime,
  endDateTime: TDateTime,
): boolean {
  const isStartTimeInProgrammingRange = isDateTimeIncludedInProgrammingRange(programmingRange, startDateTime);
  const isEndTimeInProgrammingRange = isDateTimeIncludedInProgrammingRange(programmingRange, endDateTime);
  return isStartTimeInProgrammingRange && isEndTimeInProgrammingRange;
}

function isProgrammingRangeDisplayedInRange(
  programmingRange: ProgrammingRange,
  startDateTime: TDateTime,
  endDateTime: TDateTime,
): boolean {
  if (isRangeIncludedInProgrammingRange(programmingRange, startDateTime, endDateTime)) {
    return true;
  } else if (
    isProgrammingRangeStartTimeIncludedInRange(programmingRange, startDateTime, endDateTime) ||
    isProgrammingRangeEndTimeIncludedInRange(programmingRange, startDateTime, endDateTime)
  ) {
    return true;
  }

  return false;
}

export const DEFAULT_PROGRAM_ID: ProgramId = brandNumber(0);
const DEFAULT_PROGRAM_NAME: string = "Default program";

function buildDefaultRange(startTime: TDateTime, endTime: TDateTime): IDisplayedProgrammingRange {
  return {
    uuid: brandString(v4()),
    program: {
      id: DEFAULT_PROGRAM_ID,
      name: DEFAULT_PROGRAM_NAME,
      frontDisplayColor: defaultProgramColorName,
    },
    start: startTime,
    end: endTime,
    displayedStartTime: startTime,
    displayedEndTime: endTime,
  };
}

function mapProgrammingRange(
  range: ProgrammingRange,
  displayedEndTime: TDateTime,
  displayedStartTime: TDateTime = range.start,
): IDisplayedProgrammingRange {
  return {
    ...range,
    displayedStartTime,
    displayedEndTime,
  };
}

function filterAndFormatProgrammingRanges(
  programmingRanges: ProgrammingRange[],
  filterStartTime: TDateTime,
  filterEndTime: TDateTime,
): IDisplayedProgrammingRange[] {
  const filterStartMilliseconds = $date.getDateMilliseconds(filterStartTime);
  const filterEndMilliseconds = $date.getDateMilliseconds(filterEndTime);

  return chain(programmingRanges)
    .filter((r) => isProgrammingRangeDisplayedInRange(r, filterStartTime, filterEndTime))
    .sortBy((r) => $date.getDateMilliseconds(r.start))
    .map((r) => {
      const displayedStartTime =
        $date.getDateMilliseconds(r.start) < filterStartMilliseconds ? filterStartTime : r.start;

      const rangeEndTime = r.end ?? filterEndTime;
      const displayedEndTime =
        $date.getDateMilliseconds(rangeEndTime) > filterEndMilliseconds ? filterEndTime : rangeEndTime;

      return mapProgrammingRange(r, displayedEndTime, displayedStartTime);
    })
    .value();
}

function fillProgrammingRangesGapsWithDefaultProgram(
  programmingRanges: IDisplayedProgrammingRange[],
  filterStartTime: TDateTime,
  filterEndTime: TDateTime,
): IDisplayedProgrammingRange[] {
  if (!programmingRanges.length) {
    return [buildDefaultRange(filterStartTime, filterEndTime)];
  }

  const result: IDisplayedProgrammingRange[] = [];
  let previousEnd = filterStartTime;

  for (const r of programmingRanges) {
    const currStart = r.displayedStartTime;

    if ($date.getDateMilliseconds(currStart) > $date.getDateMilliseconds(previousEnd)) {
      result.push(buildDefaultRange(previousEnd, currStart));
    }

    result.push(r);
    previousEnd = r.displayedEndTime;
  }

  if ($date.getDateMilliseconds(previousEnd) < $date.getDateMilliseconds(filterEndTime)) {
    // if filtered ranges end reached and programming range does not last until filterEndTime,
    result.push(buildDefaultRange(previousEnd, filterEndTime));
  }

  return result;
}

export function buildDisplayedRanges(
  sectionProgrammingRanges: ProgrammingRange[],
  filterStartTime: Ref<Date>,
  filterEndTime: Ref<Date>,
): IDisplayedProgrammingRange[] {
  const _filterStartTime = toValue(filterStartTime);
  const _filterEndTime = toValue(filterEndTime);

  const filterStartDateTime = $date.formatDateWithoutTimezone(_filterStartTime);
  const filterEndDateTime = $date.formatDateWithoutTimezone(_filterEndTime);

  const ranges = filterAndFormatProgrammingRanges(sectionProgrammingRanges, filterStartDateTime, filterEndDateTime);
  return fillProgrammingRangesGapsWithDefaultProgram(ranges, filterStartDateTime, filterEndDateTime);
}
